<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from 'vue';
import Data from '@/api/data';
import GenericGauge from '@/components/widgets/gauge/PlotlyGuage.vue';
import SetupCalculation from '@/components/modals/SetupCalculation.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  },
  widgetTitle: {
    type: String,
    default: 'Preview'
  }
});

const emit = defineEmits(['update:modelValue']);

// Local state
const config = ref({ ...props.modelValue });
const loadingData = ref(false);
const subcollections = ref({});
const validFields = ref([]);
const showCalcModal = ref(false);

// Lifecycle
onMounted(async () => {
  await getSubcollections();
  
  // If editing existing gauge, populate validFields
  if (config.value.source) {
    updateValidFields(config.value.source);
  }
});

// API Methods
const getSubcollections = async () => {
  loadingData.value = true;
  try {
    const response = await Data.get(props.dataID);
    if (response.body.status === 'success') {
      const ignoreSubcollections = ['Net_Cfg', 'action_request', 'action_result', 'location', 'messages'];
      subcollections.value = Object.fromEntries(
        Object.entries(response.body.details)
          .filter(([key]) => !ignoreSubcollections.includes(key))
          .sort(([a], [b]) => a.localeCompare(b))
      );
    }
  } catch (err) {
    console.error('Error fetching subcollections:', err);
  } finally {
    loadingData.value = false;
  }
};

const updateValidFields = (source) => {
  if (source && subcollections.value[source]) {
    validFields.value = Object.entries(subcollections.value[source])
      .filter(([, type]) => type === 'int' || type === 'float' || type === 'bool' || type === 'NoneType')
      .map(([field]) => field)
      .sort((a, b) => a.localeCompare(b));
  } else {
    validFields.value = [];
  }
};

const handleSourceChange = (newSource) => {
  if (newSource === '__new_source__') {
    showCalcModal.value = true;
    return;
  }
  config.value.source = newSource;
  updateValidFields(newSource);
  
  // Auto-select field if only one option
  if (validFields.value.length === 1) {
    config.value.field = validFields.value[0];
  } else {
    config.value.field = '';
  }
  
  emitUpdate();
};

const emitUpdate = () => {
  emit('update:modelValue', config.value);
};

const handleNewSource = ({ name }) => {
  showCalcModal.value = false;
  getSubcollections().then(() => {
    handleSourceChange(name.replace(/\s+/g, '_'));
  });
};

// Preview template for GenericGauge with fake data
const previewTemplate = computed(() => {
  const minValue = config.value.range?.[0] || 0;
  const maxValue = config.value.range?.[1] || 100;
  const fakeValue = minValue + (maxValue - minValue) * 0.75; // 75% of range
  
  return {
    title: props.widgetTitle || 'Preview',
    source: 'preview',
    field: 'value',
    value: fakeValue, // Use fake value for preview
    unit: config.value.unit || '',
    valuePrecision: config.value.valuePrecision || '.2f',
    range: config.value.range || [0, 100],
    color: config.value.color || '#000000',
    bgColor: config.value.bgColor || 'white',
    steps: config.value.steps || []
  };
});

// Methods for gauge steps
const defaultStepColors = [
  '#0cac2c', // Green
  '#0055ff', // Blue
  '#f59e0b', // Orange
  '#ff0000', // Red
  '#6929ff', // Purple
  '#d8f500', // Yellow
];

const addGaugeStep = () => {
  if (!config.value.steps) {
    config.value.steps = [];
  }
  
  const minValue = config.value.range?.[0] || 0;
  const maxValue = config.value.range?.[1] || 100;
  
  // Determine the starting min value for this step
  let stepMin = minValue;
  if (config.value.steps.length > 0) {
    // Use the last step's max value as this step's min
    const lastStep = config.value.steps[config.value.steps.length - 1];
    stepMin = lastStep.range[1];
  }
  
  // Get the color for this step (cycle through colors if we have more steps than colors)
  const colorIndex = config.value.steps.length % defaultStepColors.length;
  const stepColor = defaultStepColors[colorIndex];
  
  config.value.steps.push({
    range: [stepMin, maxValue],
    color: stepColor,
    thickness: 1
  });
  emitUpdate();
};

const removeGaugeStep = (index) => {
  config.value.steps.splice(index, 1);
  emitUpdate();
};
</script>

<template>
  <div class="space-y-6">
    <!-- Configuration Section -->
    <div class="space-y-4">
        <!-- Source and Field -->
        <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Data Source *
            <span v-if="loadingData" class="inline-flex items-center ml-2" title="Loading options...">
              <span class="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
            </span>
          </label>
          <select
            :value="config.source"
            @change="handleSourceChange($event.target.value)"
            :disabled="loadingData"
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="__new_source__">+ New Source</option>
            <option value="">Select a source...</option>
            <option v-for="source in Object.keys(subcollections)" :key="source" :value="source">
              {{ source }}
            </option>
          </select>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Field Name *
            <span v-if="!config.source" class="font-normal text-red-600 text-xs ml-1">(select source first)</span>
          </label>
          <select
            v-model="config.field"
            :disabled="!config.source || loadingData"
            @change="emitUpdate"
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Select a field...</option>
            <option v-for="field in validFields" :key="field" :value="field">
              {{ field }}
            </option>
          </select>
        </div>
      </div>

      <!-- Unit and Color -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Unit
          </label>
          <input
            v-model="config.unit"
            type="text"
            placeholder="e.g., °F"
            @input="emitUpdate"
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Gauge Color
          </label>
          <input
            v-model="config.color"
            type="color"
            @input="emitUpdate"
            class="w-full h-10 px-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
          />
        </div>
      </div>

      <!-- Range -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Min Value
          </label>
          <input
            v-model.number="config.range[0]"
            type="number"
            @input="emitUpdate"
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Max Value
          </label>
          <input
            v-model.number="config.range[1]"
            type="number"
            @input="emitUpdate"
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <!-- Gauge Steps -->
      <div>
        <div class="flex justify-between items-center mb-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Color Steps (Optional)
          </label>
        </div>
        
        <div v-for="(step, index) in config.steps" :key="index" class="flex gap-2 mb-2">
          <input
            v-model.number="step.range[0]"
            type="number"
            placeholder="Min"
            @input="emitUpdate"
            class="w-1/4 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
          <input
            v-model.number="step.range[1]"
            type="number"
            placeholder="Max"
            @input="emitUpdate"
            class="w-1/4 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
          <input
            v-model="step.color"
            type="color"
            @input="emitUpdate"
            class="w-1/4 px-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
          />
          <button
            @click="removeGaugeStep(index)"
            type="button"
            class="px-3 py-1 text-red-500 font-bold rounded text-sm"
          >
            Remove
          </button>
        </div>
        <button
          @click="addGaugeStep"
          type="button"
          class="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          + Add Step
        </button>
      </div>
    </div>

    <!-- Preview Section -->
    <div class="space-y-4">
      <h4 class="text-md font-semibold text-gray-800 dark:text-gray-200">Preview</h4>
      <div class="bg-white dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 p-4 min-h-[300px]">
        <GenericGauge
          :dataID="dataID"
          :template="previewTemplate"
        />
        <p class="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
          Preview with simulated value (75% of max). Live data will appear once widget is saved.
        </p>
      </div>
    </div>
  </div>

  <SetupCalculation
    v-if="showCalcModal"
    :dataID="dataID"
    :subcollectionOptions="subcollections"
    @calculation-details="handleNewSource"
    @close="showCalcModal = false"
  />
</template>
